import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Quote } from 'lucide-react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

export const SocialProofSection = () => {
  const { t } = useTranslation();
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });

  const testimonials = [
    {
      name: 'Marco R.',
      company: 'CEO, PMI Logistica',
      rating: 5,
      text: 'Sistema di gestione ordini completamente automatizzato. Errori ridotti e clienti soddisfatti.',
    },
    {
      name: 'Laura B.',
      company: 'Studio Legale',
      rating: 5,
      text: 'Chatbot 24/7 che gestisce le richieste base. Finalmente ci concentriamo sui casi importanti.',
    },
    {
      name: 'Giuseppe V.',
      company: 'Ristoratore',
      rating: 5,
      text: 'Prenotazioni semplificate e integrate perfettamente. Risultati visibili da subito.',
    },
    {
      name: 'Francesca M.',
      company: 'E-commerce Manager',
      rating: 5,
      text: 'Automazione completa del customer service. Risparmio di tempo incredibile.',
    },
    {
      name: 'Andrea T.',
      company: 'Freelance Consulente',
      rating: 5,
      text: 'Assistente virtuale che gestisce gli appuntamenti. Semplice ed efficace.',
    },
    {
      name: 'Comune di Verona',
      company: 'Ente Pubblico',
      rating: 5,
      text: 'Sistema di gestione pratiche digitale. Servizio ai cittadini migliorato notevolmente.',
    },
    {
      name: 'Silvia P.',
      company: 'Startup Tech',
      rating: 5,
      text: 'Soluzione su misura per la nostra app. Team competente e veloce.',
    },
    {
      name: 'Roberto C.',
      company: 'Azienda Manifatturiera',
      rating: 5,
      text: 'Automazione del magazzino. Efficienza aumentata del 40%.',
    },
    {
      name: 'Chiara D.',
      company: 'Libera Professionista',
      rating: 5,
      text: 'Gestione clienti automatizzata. Ora posso concentrarmi sul mio lavoro.',
    },
    {
      name: 'Michele G.',
      company: 'Catena Retail',
      rating: 5,
      text: 'Sistema inventario intelligente. Niente più errori manuali.',
    },
    {
      name: 'Elena F.',
      company: 'Agenzia Marketing',
      rating: 5,
      text: 'Automazione reportistica clienti. Tempo risparmiato per creatività.',
    },
    {
      name: 'Paolo N.',
      company: 'Gruppo Immobiliare',
      rating: 5,
      text: 'Gestione appuntamenti e visite automatizzata. Organizzazione perfetta.',
    },
  ];

  const stats = [
    { value: '50+', label: t('socialProof.stats.projects') },
    { value: '98%', label: t('socialProof.stats.satisfaction') },
    { value: '24/7', label: t('socialProof.stats.support') },
    { value: '100%', label: t('socialProof.stats.solutions') },
  ];

  return (
    <section className="py-24 px-6 bg-zinc-950 relative overflow-hidden">
      {/* Dot matrix pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }}></div>

      {/* Decorative blurs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/[0.02] rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/[0.02] rounded-full blur-3xl"></div>

      {/* Decorative tech shapes */}
      <div className="absolute top-20 right-40 w-24 h-24 border border-white/5 rounded-none rotate-12"></div>
      <div className="absolute bottom-20 left-40 w-16 h-16 border border-white/5 rounded-full"></div>

      <div className="container mx-auto max-w-7xl relative z-10">
        <div
          ref={ref}
          className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center text-white">
            {t('socialProof.title')}{' '}
            <span className="text-gray-500">
              {t('socialProof.titleGray')}
            </span>
          </h2>

          <p className="text-xl text-gray-500 text-center mb-16 max-w-3xl mx-auto">
            {t('socialProof.subtitle')}
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`p-6 rounded-none bg-black/50 border border-zinc-800 text-center transform transition-all duration-700 delay-${index * 100} ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`transform transition-all duration-700 delay-${(index + 1) * 100} ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
                }`}
              >
                <div className="group p-5 rounded-none bg-black/50 border border-zinc-800 hover:border-zinc-700 hover:bg-black/70 transition-all duration-300 h-full flex flex-col">
                  <Quote className="h-7 w-7 text-white/20 mb-3" />

                  <div className="flex gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  <p className="text-gray-400 mb-4 flex-1 leading-relaxed text-sm">{testimonial.text}</p>

                  <div className="border-t border-zinc-800 pt-3">
                    <p className="text-white font-semibold text-sm">{testimonial.name}</p>
                    <p className="text-xs text-gray-500">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};